import { CheckboxInput } from '@newfront-insurance/core-ui';
import { Button, Input } from '@newfront-insurance/core-ui/v2';
import { Loader2, Plus, X } from 'lucide-react';
import { useState } from 'react';

export interface EmailGroup {
  id: string;
  displayName: string;
  mail: string;
}

export interface SharedInbox {
  id: string;
  email: string;
  enabled: boolean;
}

export interface OutlookMailboxSettingsProps {
  groups: EmailGroup[];
  selectedGroupIds: Set<string>;
  sharedInboxes: SharedInbox[];
  isLoading: boolean;
  error: string | null;
  toggleGroup: (groupId: string) => void;
  addSharedInbox: (email: string) => boolean;
  removeSharedInbox: (id: string) => void;
  toggleSharedInbox: (id: string) => void;
}

export function OutlookMailboxSettings({
  groups,
  selectedGroupIds,
  sharedInboxes,
  isLoading,
  error,
  toggleGroup,
  addSharedInbox,
  removeSharedInbox,
  toggleSharedInbox,
}: OutlookMailboxSettingsProps) {
  const [newInboxEmail, setNewInboxEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
        <span className="text-muted-foreground ml-2 text-sm">Loading email groups...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  const handleAddInbox = () => {
    setEmailError('');
    if (!newInboxEmail.trim()) {
      setEmailError('Please enter an email address');
      return;
    }
    const success = addSharedInbox(newInboxEmail);
    if (success) {
      setNewInboxEmail('');
    } else {
      setEmailError('Invalid email or already exists');
    }
  };

  return (
    <div className="flex flex-col space-y-6 py-4">
      {/* Email Groups Section */}
      <div className="flex flex-col space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-medium">Email Group Access</h3>
            <p className="text-muted-foreground text-xs">Select the email groups you want to give Nova access to:</p>
          </div>
          <div className="text-muted-foreground text-xs whitespace-nowrap">
            {selectedGroupIds.size} group{selectedGroupIds.size !== 1 ? 's' : ''} selected
          </div>
        </div>

        {groups.length > 0 ? (
          <div className="max-h-60 space-y-3 overflow-y-auto pr-2">
            {groups.map((group) => (
              <div key={group.id} className="hover:bg-muted/50 bg-steel-100 flex items-start gap-3 rounded-md p-3">
                <CheckboxInput
                  id={`group-${group.id}`}
                  checked={selectedGroupIds.has(group.id)}
                  onChange={(_e) => toggleGroup(group.id)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                />
                <label htmlFor={`group-${group.id}`} className="flex-1 cursor-pointer space-y-1 pl-1">
                  <div className="text-sm leading-none font-medium">{group.displayName}</div>
                  <div className="text-muted-foreground text-xs">{group.mail}</div>
                </label>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-2">
            <p className="text-muted-foreground text-sm">No email groups found.</p>
          </div>
        )}
      </div>

      {/* Shared Inboxes Section */}
      <div className="flex flex-col space-y-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium">Shared Inbox Access</h3>
          <p className="text-muted-foreground text-xs">Add shared inboxes you want Nova to access.</p>
        </div>

        <div className="flex flex-col space-y-3">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="shared-inbox@example.com"
                value={newInboxEmail}
                onChange={(e) => {
                  setNewInboxEmail(e.target.value);
                  setEmailError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddInbox();
                  }
                }}
              />
              {emailError && <p className="text-destructive mt-1 text-xs">{emailError}</p>}
            </div>
            <Button onClick={handleAddInbox} size="default" className="h-10 w-10 flex-shrink-0 p-0">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {sharedInboxes.length > 0 && (
            <div className="space-y-2 overflow-x-hidden overflow-y-auto">
              {sharedInboxes.map((inbox) => (
                <div key={inbox.id} className="hover:bg-muted/50 bg-steel-100 flex items-center gap-3 rounded-md p-3">
                  <div className="flex flex-1 items-center gap-3">
                    <CheckboxInput
                      id={`inbox-${inbox.id}`}
                      checked={inbox.enabled}
                      onChange={(_e) => toggleSharedInbox(inbox.id)}
                      className="h-4 w-4 flex-shrink-0"
                    />
                    <label htmlFor={`inbox-${inbox.id}`} className="flex-1 cursor-pointer">
                      <div className="text-sm leading-tight break-all">{inbox.email}</div>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSharedInbox(inbox.id)}
                    className="hover:bg-destructive/10 text-muted-foreground hover:text-destructive flex-shrink-0 rounded-sm p-1.5 transition-colors"
                    title="Remove inbox"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
